import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Avatar from '../../../../shared/_TBComponents/Avatar/Avatar';
import Button from '../../../../shared/_TBComponents/Button';
import Input from '../../../../shared/_TBComponents/Input';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import ProfilePicUploadButton from '../../../../shared/components/ProfilePicUploadButton';
import { COUNTRY_CODES } from '../../../../shared/constants';
import IconActionButton from './IconActionButton';

const PlaceholderAvatar = styled.div`
  width: 140px;
  height: 140px;
  min-width: 140px;
  border-radius: 50%;
  overflow: hidden;
  background: #E5E5E5;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TopWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 42px;

  input:not([disabled]), textarea:not([disabled]) {
    border-color: #D0D0D0 !important;
  }

  input:not([disabled]):focus, textarea:not([disabled]):focus {
    outline: none !important;
    border-color: #77A7FF !important;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

const AvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormContainer = styled.div`
  flex: 1;
  min-width: 260px;

  @media (max-width: 767px) {
    width: 100%;
    min-width: 0;
  }
`;

const ErrorWrapper = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: #ff6600;
  max-width: 240px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 140px;
  height: 140px;
`;

const UploadWrapper = styled.div`
  position: absolute;
  top: -21px;
  right: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  gap: 20px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-bottom: 16px;
  
  input:not([disabled]), textarea:not([disabled]) {
    border-color: #D0D0D0 !important;
  }
  
  input:not([disabled]):focus, textarea:not([disabled]):focus {
    outline: none !important;
    border-color: #77A7FF !important;
  }
`;

const MobileGroup = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-end;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const GroupWrapper = styled.div`
  display: flex;
  flex: 2;
  gap: 20px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 0;
    width: 100%;
  }
`;

const ContactCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 10px;
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  margin-bottom: 6px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
`;

const ContactInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContactActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ContactsContainer = styled.div`
  margin-top: 5px;
`;

const Separator = styled.div`
  align-self: stretch;
  background-color: #cccccc;
  width: 1.5px;
  box-sizing: border-box;
  margin: ${(props) => (props.isIconButtons ? '4px 4px' : '0')};
`;

const IntegrationCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 10px 10px;
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  height: 45px;
  width: 35%;

  &:focus-within {
    border-color: #77A7FF;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const IntegrationInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 12px;
`;

const IntegrationInputWrapper = styled.div`
  position: relative;
  flex: 1;
  
  input {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  input:focus {
    border: none !important;
    outline: none !important;
  }
`;

const ClickableLabel = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  gap: 8px;
  cursor: pointer;
  padding-top: 8px;
  transition: color 0.2s;
  
  &:hover {
    color: #77A7FF;
  }
  
  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s;
  }
`;

const AthleteProfileForm = ({
  athlete,
  contacts,
  groups,
  selectBoxStyles,
  onOpenNestedModal,
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  errors,
  touched,
  isNew,
  usernamesEnabled,
  scrollContainerRef,
}) => {
  const isProfileImageUpdateLoading = useSelector(
    (state) => state.navigator.ui.isUpdatingAthleteImage,
  );
  const imageError = useSelector((state) => state.navigator.data.athleteImageError);

  const [showPasswordFields, setShowPasswordFields] = useState(isNew);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPIN, setShowPIN] = useState(false);
  const [showConfirmPIN, setShowConfirmPIN] = useState(false);

  return (
    <>
      <TopWrapper>
        <AvatarContainer>
          {(() => {
            if (isNew) {
              return (
                <PlaceholderAvatar>
                  {athlete.pic && <img src={athlete.pic} alt='account avatar' />}
                </PlaceholderAvatar>
              );
            }
            if (isProfileImageUpdateLoading) {
              return (
                <SpinnerWrapper>
                  <Spinner />
                </SpinnerWrapper>
              );
            }
            return (
              <Avatar
                avatar={athlete.pic}
                name={`${athlete.first} ${athlete.last}`}
                size={140}
              >
                <UploadWrapper>
                  <ProfilePicUploadButton userId={athlete?.id} />
                </UploadWrapper>
              </Avatar>
            );
          })()}
          {!isNew && imageError ? <ErrorWrapper>{imageError}</ErrorWrapper> : null}
        </AvatarContainer>

        <FormContainer>
          <Row>
            <FieldWrapper>
              <Input
                label='First Name'
                name='first'
                type='text'
                value={values.first}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='First Name'
                fontSize='14px'
                height='38px'
                width='100%'
                styledPropsInput={{ autoComplete: 'given-name' }}
                styledPropsLabel={{ fontWeight: 700 }}
                error={!!(touched.first && errors.first)}
                errorMsg={errors.first || ''}
                right='5px'
                top='38px'
              />
            </FieldWrapper>
            <FieldWrapper>
              <Input
                label='Last Name'
                name='last'
                type='text'
                value={values.last}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Last Name'
                fontSize='14px'
                height='38px'
                width='100%'
                styledPropsInput={{ autoComplete: 'family-name' }}
                styledPropsLabel={{ fontWeight: 700 }}
                error={!!(touched.last && errors.last)}
                errorMsg={errors.last || ''}
                right='5px'
                top='38px'
              />
            </FieldWrapper>
          </Row>

          <Row>
            <FieldWrapper>
              <Input
                label={usernamesEnabled ? 'Username' : 'Email Address'}
                name='email'
                type={usernamesEnabled ? 'text' : 'email'}
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={usernamesEnabled ? 'Enter username' : 'email@domain.com'}
                fontSize='14px'
                height='38px'
                width='100%'
                styledPropsInput={{ autoComplete: usernamesEnabled ? 'username' : 'email' }}
                styledPropsLabel={{ fontWeight: 700 }}
                error={!!(touched.email && errors.email)}
                errorMsg={errors.email || ''}
                right='5px'
                top='38px'
              />
            </FieldWrapper>
            <FieldWrapper>
              <Input
                label='One Login ID'
                name='ssoId'
                type='text'
                value={values.ssoId}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='SAML/SSO ID'
                fontSize='14px'
                height='38px'
                width='100%'
                styledPropsInput={{ autoComplete: 'off' }}
                styledPropsLabel={{ fontWeight: 700 }}
              />
            </FieldWrapper>
          </Row>
        </FormContainer>
      </TopWrapper>

      <Row>
        <FieldWrapper style={{ flex: 2 }}>
          <Text styledTextProps={{ marginBottom: 0, fontWeight: 700 }}>
            Mobile Number
          </Text>
          <MobileGroup>
            <SelectBox
              options={COUNTRY_CODES.map(({ value, label }) => (
                { id: value, name: label }))}
              value={COUNTRY_CODES.map(({ value, label }) => (
                { id: value, name: label })).find(
                (o) => o.id === values.mobileCountry,
              ) || null}
              onChangeFunction={(opt) => setFieldValue('mobileCountry', opt ? opt.id : '')}
              placeholder='Country code'
              customStyles={selectBoxStyles}
            />
            <Input
              name='mobileNumber'
              type='tel'
              value={values.mobileNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder='1234567890'
              fontSize='14px'
              width='100%'
            />
          </MobileGroup>
        </FieldWrapper>

        <GroupWrapper>
          <FieldWrapper style={{ flex: 2 }}>
            <SelectBox
              title='Gender'
              options={[
                { id: '1', name: 'Male' },
                { id: '2', name: 'Female' },
                { id: '3', name: 'Other' },
              ]}
              value={values.gender}
              onChangeFunction={(opt) => setFieldValue('gender', opt)}
              placeholder='Select gender'
              width='100%'
              customStyles={selectBoxStyles}
              titleStyledProps={{ fontWeight: 700 }}
            />
          </FieldWrapper>
          <FieldWrapper style={{ flex: 2 }}>
            <Input
              label='External ID'
              name='externalId'
              value={values.externalId}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder='Org/Ext. Platform ID'
              fontSize='14px'
              width='100%'
              styledPropsLabel={{ fontWeight: 700 }}
              styledPropsInput={{ autoComplete: 'off' }}
            />
          </FieldWrapper>
        </GroupWrapper>
      </Row>

      <Row>
        <FieldWrapper>
          <ClickableLabel onClick={() => setShowPasswordFields(!showPasswordFields)}>
            <Text styledTextProps={{ fontWeight: 700, color: 'inherit' }}>{isNew ? 'Password and PIN' : 'Set Password and PIN'}</Text>
            <svg
              viewBox='0 0 20 20'
              fill='currentColor'
              style={{ transform: showPasswordFields ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <path
                fillRule='evenodd'
                d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                clipRule='evenodd'
              />
            </svg>
          </ClickableLabel>
        </FieldWrapper>
      </Row>

      <Row style={{ display: showPasswordFields ? 'flex' : 'none' }}>
        <FieldWrapper>
          <Input
            label={isNew && !usernamesEnabled ? (
              <>
                Password
                {' '}
                <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>
                  (Leave blank for random password)
                </span>
              </>
            ) : isNew ? 'Password' : 'Change Password'}
            name='changePassword'
            type={showPassword ? 'text' : 'password'}
            value={values.changePassword}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='Enter new password'
            fontSize='14px'
            height='38px'
            width='100%'
            styledPropsInput={{ autoComplete: 'new-password' }}
            styledPropsLabel={{ fontWeight: 700 }}
            error={!!(touched.changePassword && errors.changePassword)}
            errorMsg={errors.changePassword || ''}
            right='5px'
            top='38px'
            showEyeToggle
            onEyeToggleClick={() => setShowPassword(!showPassword)}
            isPasswordVisible={showPassword}
            eyeRight='0px'
            eyeTop='33px'
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            label='Confirm Password'
            name='confirmChangePassword'
            type={showConfirmPassword ? 'text' : 'password'}
            value={values.confirmChangePassword}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='Confirm new password'
            fontSize='14px'
            height='38px'
            width='100%'
            styledPropsInput={{ autoComplete: 'new-password' }}
            styledPropsLabel={{ fontWeight: 700 }}
            error={!!(touched.confirmChangePassword && errors.confirmChangePassword)}
            errorMsg={errors.confirmChangePassword || ''}
            right='5px'
            top='38px'
            showEyeToggle
            onEyeToggleClick={() => setShowConfirmPassword(!showConfirmPassword)}
            isPasswordVisible={showConfirmPassword}
            eyeRight='0px'
            eyeTop='33px'
          />
        </FieldWrapper>
      </Row>
      <Row style={{ display: showPasswordFields ? 'flex' : 'none' }}>
        <FieldWrapper>
          <Input
            label={(
              <>
                {isNew ? 'PIN' : 'Change PIN'}
                {' '}
                <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>
                  (Optional - Weight Room View)
                </span>
              </>
            )}
            name='changePIN'
            type={showPIN ? 'text' : 'password'}
            value={values.changePIN}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='Enter new PIN'
            fontSize='14px'
            height='38px'
            width='100%'
            styledPropsInput={{ maxLength: 4, autoComplete: 'new-password' }}
            styledPropsLabel={{ fontWeight: 700 }}
            error={!!(touched.changePIN && errors.changePIN)}
            errorMsg={errors.changePIN || ''}
            right='5px'
            top='38px'
            showEyeToggle
            onEyeToggleClick={() => setShowPIN(!showPIN)}
            isPasswordVisible={showPIN}
            eyeRight='0px'
            eyeTop='33px'
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            label='Confirm PIN'
            name='confirmChangePIN'
            type={showConfirmPIN ? 'text' : 'password'}
            value={values.confirmChangePIN}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='Confirm new PIN'
            fontSize='14px'
            height='38px'
            width='100%'
            styledPropsInput={{ maxLength: 4, autoComplete: 'new-password' }}
            styledPropsLabel={{ fontWeight: 700 }}
            error={!!(touched.confirmChangePIN && errors.confirmChangePIN)}
            errorMsg={errors.confirmChangePIN || ''}
            right='5px'
            top='38px'
            showEyeToggle
            onEyeToggleClick={() => setShowConfirmPIN(!showConfirmPIN)}
            isPasswordVisible={showConfirmPIN}
            eyeRight='0px'
            eyeTop='33px'
          />
        </FieldWrapper>
      </Row>

      <Row>
        <FieldWrapper>
          <SelectBox
            title='Groups'
            isMulti
            options={groups.map(({ id, name }) => ({ id, name }))}
            value={values.groups}
            onChangeFunction={(opt) => setFieldValue('groups', opt)}
            placeholder='Select group'
            styledProps={{ border: `1px solid ${touched.groups && errors.groups ? '#CC0404' : '#EEEEEE'}` }}
            customStyles={selectBoxStyles}
            titleStyledProps={{ fontWeight: 700 }}
            menuPortalTarget={document.body}
            onMenuOpen={() => {
              if (scrollContainerRef?.current) {
                scrollContainerRef.current.style.overflowY = 'hidden';
              }
            }}
            onMenuClose={() => {
              if (scrollContainerRef?.current) {
                scrollContainerRef.current.style.overflowY = 'auto';
              }
            }}
          />
          {touched.groups && errors.groups && (
            <Text styledTextProps={{ fontSize: '13px', fontWeight: 700, color: '#CC0404', marginTop: '5px' }}>
              {errors.groups}
            </Text>
          )}
        </FieldWrapper>
      </Row>

      <Row>
        <FieldWrapper>
          <Text styledWrapperProps={{ marginBottom: '4px', display: 'block', fontWeight: 700 }}>
            Parent/Guardian Contacts
            <Text styledTextProps={{ fontSize: '12px', color: '#9E9E9E' }}>
              Any contacts listed below will be eligible to receive Weekly Performance
              Assessment emails.
            </Text>
          </Text>
          {contacts && contacts.length > 0 && (
          <ContactsContainer>
            {contacts.map((contact, index) => (
              <ContactCard key={contact.id || index}>
                <ContactInfo>
                  <Text styledTextProps={{ fontSize: '12px', fontWeight: 600 }}>
                    {contact.name}
                  </Text>
                  <Text styledTextProps={{ fontSize: '12px', color: '#757575' }}>
                    {contact.status ? `Status: ${contact.status} - ${contact.email}` : contact.email}
                  </Text>
                </ContactInfo>
                <ContactActions>
                  <IconActionButton
                    type='edit'
                    tabIndex={-1}
                    onClick={(e) => { e.preventDefault(); onOpenNestedModal('editContact', index); }}
                  />
                  <IconActionButton
                    type='delete'
                    tabIndex={-1}
                    onClick={(e) => { e.preventDefault(); onOpenNestedModal('deleteContact', index); }}
                  />
                </ContactActions>
              </ContactCard>
            ))}
          </ContactsContainer>
          )}
          <Button
            name='Add Contact'
            icon='add-user'
            iconSize={16}
            iconColor='#FFFFFF'
            fontSize='14px'
            bgColor='#0DCC8A'
            customBorderRadius='4px'
            noBorder
            txtColor='#FFFFFF'
            styledTextProps={{ fontWeight: 600 }}
            styledWrapperProps={{ marginTop: '8px', alignSelf: 'flex-start', tabIndex: -1 }}
            onClick={(e) => { e.preventDefault(); onOpenNestedModal('addContact'); }}
          />
        </FieldWrapper>
      </Row>
      <Row>
        <FieldWrapper>
          <Text
            styledWrapperProps={{ marginBottom: '4px', fontWeight: 700 }}
          >
            Integrations
          </Text>
          <IntegrationCard>
            <Text
              styledWrapperProps={{
                paddingRight: '10px',
              }}
              styledTextProps={{
                fontSize: '14px', fontWeight: 600,
              }}
            >
              Smartabase ID
            </Text>
            <Separator />
            <IntegrationInfo>
              <IntegrationInputWrapper>
                <Input
                  name='smartabaseId'
                  type='text'
                  inputOnly
                  value={values.smartabaseId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder='ex. 1234'
                  fontSize='14px'
                  width='100%'
                />
              </IntegrationInputWrapper>
            </IntegrationInfo>
          </IntegrationCard>
        </FieldWrapper>
      </Row>
    </>
  );
};

AthleteProfileForm.propTypes = {
  athlete: PropTypes.shape({
    first: PropTypes.string,
    last: PropTypes.string,
    pic: PropTypes.string,
    email: PropTypes.string,
    ssoId: PropTypes.string,
    mobileCountry: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    mobileNumber: PropTypes.string,
    gender: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    externalId: PropTypes.string,
    groups: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    smartabaseId: PropTypes.string,
  }).isRequired,
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  selectBoxStyles: PropTypes.instanceOf(Object).isRequired,
  onOpenNestedModal: PropTypes.func.isRequired,
  values: PropTypes.shape({
    first: PropTypes.string,
    last: PropTypes.string,
    email: PropTypes.string,
    ssoId: PropTypes.string,
    mobileCountry: PropTypes.string,
    mobileNumber: PropTypes.string,
    gender: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    externalId: PropTypes.string,
    changePassword: PropTypes.string,
    confirmChangePassword: PropTypes.string,
    changePIN: PropTypes.string,
    confirmChangePIN: PropTypes.string,
    smartabaseId: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })),
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.instanceOf(Object),
  touched: PropTypes.instanceOf(Object),
  isNew: PropTypes.bool,
  usernamesEnabled: PropTypes.bool,
  scrollContainerRef: PropTypes.instanceOf(Object),
};

AthleteProfileForm.defaultProps = {
  contacts: [],
  groups: [],
  errors: {},
  touched: {},
  isNew: false,
  usernamesEnabled: false,
  scrollContainerRef: null,
};

export default AthleteProfileForm;
